/**
 * Azure Secrets Client for Frontend
 * Utility to fetch secrets from Azure Functions
 */

import axios from "axios";
import { config } from "@/config/env";
import { logger } from "@/utils/logger"

const AZURE_FUNCTIONS_URL = config.azureFunctionsUrl;
const FUNCTION_KEY = config.azureFunctionsKey; // Optional: if using function key auth

interface SecretResponse {
  success: boolean;
  data: {
    name: string;
    value: string;
  };
}

/**
 * Fetch a secret from Azure Key Vault via Azure Functions
 * @param secretName - Name of the secret to retrieve
 * @returns Promise with secret value
 */
export const getSecret = async (secretName: string): Promise<string> => {
  try {
    const url = `${AZURE_FUNCTIONS_URL}/secrets/${secretName}`;
    const params: Record<string, string> = {};

    // Add function key if configured
    if (FUNCTION_KEY) {
      params.code = FUNCTION_KEY;
    }

    // Use axios directly WITHOUT withCredentials (Azure Functions não suporta)
    const response = await axios.get<SecretResponse>(url, {
      params,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });

    if (response.data.success) {
      return response.data.data.value;
    }

    throw new Error("Failed to retrieve secret");
  } catch (error: any) {
    logger.error(`Error fetching secret '${secretName}':`, error);
    throw error;
  }
};

/**
 * Fetch multiple secrets at once
 * @param secretNames - Array of secret names
 * @returns Promise with object mapping secret names to values
 */
export const getSecrets = async (
  secretNames: string[]
): Promise<Record<string, string>> => {
  try {
    const promises = secretNames.map((name) => getSecret(name));
    const values = await Promise.all(promises);

    const secrets: Record<string, string> = {};
    secretNames.forEach((name, index) => {
      secrets[name] = values[index];
    });

    return secrets;
  } catch (error) {
    logger.error("Error fetching multiple secrets:", error);
    throw error;
  }
};

/**
 * Cache for secrets to avoid repeated calls
 */
const secretsCache = new Map<string, { value: string; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get secret with caching
 * @param secretName - Name of the secret
 * @param forceRefresh - Force refresh from server
 * @returns Promise with secret value
 */
export const getCachedSecret = async (
  secretName: string,
  forceRefresh = false
): Promise<string> => {
  const now = Date.now();
  const cached = secretsCache.get(secretName);

  // Return cached value if still valid and not forcing refresh
  if (!forceRefresh && cached && now - cached.timestamp < CACHE_TTL) {
    return cached.value;
  }

  // Fetch new value
  const value = await getSecret(secretName);
  secretsCache.set(secretName, { value, timestamp: now });

  return value;
};

/**
 * Clear secrets cache
 */
export const clearSecretsCache = (): void => {
  secretsCache.clear();
};

/**
 * Azure OpenAI Configuration
 */
export interface AzureOpenAIConfig {
  endpoint: string;
  apiKey: string;
  deploymentName: string;
}

/**
 * Get Azure OpenAI configuration from Key Vault
 * @returns Promise with Azure OpenAI config
 */
export const getAzureOpenAIConfig = async (): Promise<AzureOpenAIConfig> => {
  try {
    logger.log("[SecretsClient] Fetching Azure OpenAI configuration...");

    const secrets = await getSecrets([
      "AZURE-OPENAI-ENDPOINT",
      "AZURE-OPENAI-API-KEY",
      "AZURE-OPENAI-DEPLOYMENT-NAME",
    ]);

    const config: AzureOpenAIConfig = {
      endpoint: secrets["AZURE-OPENAI-ENDPOINT"],
      apiKey: secrets["AZURE-OPENAI-API-KEY"],
      deploymentName: secrets["AZURE-OPENAI-DEPLOYMENT-NAME"],
    };

    logger.log("[SecretsClient] ✅ Azure OpenAI config retrieved:", {
      endpoint: config.endpoint,
      deploymentName: config.deploymentName,
      hasApiKey: !!config.apiKey,
    });

    return config;
  } catch (error) {
    logger.error("[SecretsClient] Failed to get Azure OpenAI config:", error);
    throw new Error("Failed to retrieve Azure OpenAI configuration");
  }
};
